import type { B2BNavLink, SelectOption } from '../types';
import { US_CITY_OPTIONS_BY_STATE } from './us-cities';

/**
 * Lead-form select options and the shared nav/footer link copy. These are the
 * same across both B2B pages, so they live here and are spread into each page
 * config. Edit these constants to change the form dropdowns or nav/footer links
 * — no component changes required.
 */

/* ----------------------------- Lead-form selects -------------------------- */

/**
 * Industry options. The `value` is the exact HubSpot `industry_category` dropdown
 * value so it passes through to HubSpot unchanged — HubSpot silently drops any
 * value that is not one of these exact strings. Keep this list in sync with the
 * HubSpot form's `industry_category` property.
 */
export const INDUSTRY_OPTIONS: SelectOption[] = [
    { value: '3D Rendering', label: '3D Rendering' },
    { value: 'Agency / Advertising', label: 'Agency / Advertising' },
    { value: 'Architect', label: 'Architect' },
    { value: 'Architectural Designer', label: 'Architectural Designer' },
    { value: 'Builder', label: 'Builder' },
    { value: 'Consultant', label: 'Consultant' },
    { value: 'Consumer', label: 'Consumer' },
    { value: 'Contractor', label: 'Contractor' },
    { value: 'Design & Build', label: 'Design & Build' },
    { value: 'Draftsman', label: 'Draftsman' },
    { value: 'Engineer', label: 'Engineer' },
    { value: 'Industry Associations', label: 'Industry Associations' },
    { value: 'Interior Designer', label: 'Interior Designer' },
    { value: 'Kitchen Designer', label: 'Kitchen Designer' },
    { value: 'Landscape Architect', label: 'Landscape Architect' },
    { value: 'Manufacturer/Supplier', label: 'Manufacturer/Supplier' },
    { value: 'Other Services', label: 'Other Services' },
    { value: 'Project Manager', label: 'Project Manager' },
    { value: 'Student', label: 'Student' },
    {
        value: 'Timber Joiner, Kitchen Cabinetmaker',
        label: 'Timber Joiner, Kitchen Cabinetmaker',
    },
];

/** US states (the B2B pages target the USA market entry). */
export const STATE_OPTIONS: SelectOption[] = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
];

/**
 * Major cities keyed by state value — sourced from {@link us-cities.ts}
 * which ships the top ~50 cities for each of the 50 US states (≈2,500
 * entries, ~25 KB gzipped). States without an explicit list fall back
 * to {@link DEFAULT_CITY_OPTIONS}.
 */
export const CITY_OPTIONS_BY_STATE: Record<string, SelectOption[]> =
    US_CITY_OPTIONS_BY_STATE;

export const DEFAULT_CITY_OPTIONS: SelectOption[] = [
    { value: 'capital-city', label: 'Capital City' },
    { value: 'other', label: 'Other' },
];

/** Returns the city options for a given state value, with a sensible fallback. */
export const getCityOptions = (stateValue: string): SelectOption[] => {
    if (!stateValue) return [];
    return CITY_OPTIONS_BY_STATE[stateValue] ?? DEFAULT_CITY_OPTIONS;
};

/* ------------------------------ Nav + footer ------------------------------ */

export const B2B_NAV_LINKS: B2BNavLink[] = [
    { label: 'Projects', href: '/projects' },
    { label: 'Products', href: '/products' },
    { label: 'Professionals', href: '/professionals' },
    { label: 'Articles', href: '/articles' },
];

export const B2B_NAV_TAGLINE =
    'Browse projects, find products, connect with professionals.';

export const B2B_FOOTER_LINK_GROUPS = [
    {
        heading: 'Discover',
        links: [
            { label: 'Projects', href: '/projects' },
            { label: 'Products', href: '/products' },
            { label: 'Professionals', href: '/professionals' },
            { label: 'Archizeen', href: '/articles' },
        ],
    },
    {
        heading: 'Business',
        links: [
            { label: 'Become a Pro', href: '/b2b/professionals' },
            { label: 'For Suppliers & Brands', href: '/b2b/brands' },
        ],
    },
    {
        heading: 'ArchiPro',
        links: [
            { label: 'About', href: '/about-us' },
            { label: 'Careers', href: '/careers', isNonRemixRoute: true },
            {
                label: 'Contact',
                href: '/contact-us',
                isNonRemixRoute: true,
            },
        ],
    },
    {
        heading: 'Connect',
        links: [
            { label: 'Home Design Evening', href: '/home-design-event' },
            { label: 'Instagram', href: 'https://www.instagram.com/archipro' },
            { label: 'Facebook', href: 'https://www.facebook.com/archipro' },
            {
                label: 'Linkedin',
                href: 'https://www.linkedin.com/company/archipro',
            },
            { label: 'YouTube', href: 'https://www.youtube.com/archipro' },
        ],
    },
];

export const B2B_FOOTER_LEGAL_LINKS: B2BNavLink[] = [];
